import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronLeft, ChevronRight, AlertTriangle,
  Type, Share2, Bookmark, X, Volume2, Pause, RotateCcw, Gauge,
} from "lucide-react";
import { fetchChapter, findBook, getCurrentVersion, neighbourChapters } from "../lib/bible";
import { trackBibleChapter } from "../lib/readingProgress";
import {
  isFavoritePassage, toggleFavoritePassage,
  markTodayCompleted,
} from "../lib/dailyJourney";
import useTTS from "../lib/useTTS";
import JournalPrompt from "../components/JournalPrompt";
import ShareVerseModal from "../components/ShareVerseModal";
import ShareButton from "../features/share/ShareButton";
import { Skeleton } from "../components/ui/skeleton";

// ---- Reading settings ----
const SETTINGS_KEY = "cm_reading_settings_v1";
const FONT_SIZES = {
  small:  { label: "Pequeno", cls: "text-[15px] leading-[1.8]" },
  medium: { label: "Médio",   cls: "text-[17px] leading-[1.85]" },
  large:  { label: "Grande",  cls: "text-[19px] leading-[1.9]" },
};
const DEFAULT_SIZE = "medium";

const readSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && FONT_SIZES[parsed.fontSize]) return parsed;
  } catch {}
  return { fontSize: DEFAULT_SIZE };
};

const writeSettings = (next) => {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch {}
};

export default function BibliaCapitulo() {
  const { book: slug, chapter: chapterParam } = useParams();
  const [searchParams] = useSearchParams();
  const focusVerse = Number.parseInt(searchParams.get("v") || "", 10) || null;

  const version = useMemo(() => getCurrentVersion(), []);
  const book = useMemo(() => findBook(slug), [slug]);
  const requested = Number.parseInt(chapterParam, 10);
  const { prev, next } = useMemo(
    () => neighbourChapters(slug, chapterParam),
    [slug, chapterParam]
  );

  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [chapter, setChapter] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [settings, setSettings] = useState(readSettings);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedVerse, setSelectedVerse] = useState(focusVerse);
  const [shareVerse, setShareVerse] = useState(null); // { text, reference }
  const versesRef = useRef({});
  const tts = useTTS();

  // Fetch chapter whenever slug/chapter changes
  useEffect(() => {
    if (!book || !requested) {
      setStatus("error");
      setErrorCode("not-found");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    setChapter(null);
    setErrorCode(null);
    fetchChapter(slug, requested)
      .then((ch) => {
        if (cancelled) return;
        setChapter(ch);
        setStatus("ready");
        trackBibleChapter({
          book_slug: book.slug,
          book_name: book.name,
          chapter: requested,
          version: version.code,
        });
        // Streak: reading any Bible chapter counts as today's spiritual reading.
        markTodayCompleted();
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorCode(err?.message || "unavailable");
        setStatus("error");
      });
    return () => { cancelled = true; };
  }, [slug, requested, book, version.code]);

  // Auto-scroll to the focused verse when the chapter lands
  useEffect(() => {
    if (status !== "ready" || !focusVerse) return;
    const el = versesRef.current[focusVerse];
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
    }
  }, [status, focusVerse, chapter]);

  const applyFontSize = (size) => {
    const next = { ...settings, fontSize: size };
    setSettings(next);
    writeSettings(next);
  };

  const verseRef = (num) =>
    `${book?.name || ""} ${requested}${num ? `:${num}` : ""}`.trim();

  const verseHref = (num) =>
    `${window.location.origin}/biblia/${slug}/${requested}?v=${num}`;

  const handleShareVerse = (v) => {
    setShareVerse({ text: v.text, reference: verseRef(v.verse) });
  };

  const handleToggleFavorite = (v) => {
    const now = toggleFavoritePassage({
      ref: verseRef(v.verse),
      href: `/biblia/${slug}/${requested}?v=${v.verse}`,
    });
    toast(now ? "Adicionado aos favoritos." : "Removido dos favoritos.");
    setSettings((s) => ({ ...s })); // force re-render for Bookmark fill state
  };

  if (!book) {
    return (
      <div className="max-w-3xl mx-auto section-padding py-10" data-testid="bible-chapter-not-found">
        <p className="font-display text-2xl text-navy">Capítulo não encontrado.</p>
        <Link
          to="/biblia"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold mt-6"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar para Bíblia
        </Link>
      </div>
    );
  }

  const fontCls = (FONT_SIZES[settings.fontSize] || FONT_SIZES[DEFAULT_SIZE]).cls;

  const ttsItems = chapter?.verses?.map((v) => ({
    id: `v-${v.verse}`,
    text: `${v.verse}. ${v.text}`,
  })) || [];
  const onTTSClick = () => {
    if (!ttsItems.length) return;
    if (tts.playing && !tts.paused) tts.pause();
    else if (tts.playing && tts.paused) tts.resume();
    else tts.speak(ttsItems);
  };
  const ttsCurrentVerse = (() => {
    const id = tts.currentId;
    if (!id || !id.startsWith("v-")) return null;
    return Number.parseInt(id.slice(2), 10) || null;
  })();

  return (
    <div className="max-w-3xl mx-auto section-padding py-8" data-testid="bible-chapter-page">
      <div className="flex items-center justify-between mb-6 gap-3">
        <Link
          to={`/biblia/${book.slug}`}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy"
          data-testid="bible-chapter-back"
        >
          <ArrowLeft className="w-4 h-4" /> {book.name}
        </Link>
        <div className="flex items-center gap-2">
          {tts.supported && (
            <button
              type="button"
              onClick={onTTSClick}
              disabled={!ttsItems.length}
              aria-label={tts.playing && !tts.paused ? "Pausar leitura" : "Ouvir capítulo"}
              data-testid="bible-chapter-tts"
              data-state={tts.playing ? (tts.paused ? "paused" : "playing") : "idle"}
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors disabled:opacity-40 ${
                tts.playing
                  ? "bg-gold border-gold text-navy"
                  : "bg-white border-border text-navy/60 hover:text-gold"
              }`}
            >
              {tts.playing && !tts.paused ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowSettings(true)}
            aria-label="Configurações de leitura"
            data-testid="bible-chapter-settings"
            className="w-9 h-9 rounded-full bg-white border border-border flex items-center justify-center text-navy/60 hover:text-gold transition-colors"
          >
            <Type className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          Versão {version.name}
        </p>
        <h1
          className="font-display text-4xl sm:text-5xl text-navy mt-2 leading-[1.05]"
          data-testid="bible-chapter-title"
        >
          {book.name}, Capítulo {requested}
        </h1>
      </div>

      {/* Content states */}
      {status === "loading" && (
        <div className="bg-white border border-border rounded-2xl p-6 sm:p-8 space-y-4" data-testid="bible-chapter-loading">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-5/6" />
          <Skeleton className="h-5 w-4/5" />
          <Skeleton className="h-5 w-full" />
        </div>
      )}

      {status === "error" && (
        <div
          className="bg-[hsl(var(--parchment))] border border-border rounded-2xl p-8 text-center"
          data-testid="bible-chapter-error"
        >
          <AlertTriangle className="w-6 h-6 text-gold mx-auto" />
          <p className="font-display text-xl text-navy mt-3">
            {errorCode === "not-found"
              ? "Capítulo não encontrado."
              : "Não foi possível carregar o texto bíblico."}
          </p>
          <p className="text-navy/60 text-sm mt-2">
            {errorCode === "not-found"
              ? "Verifique a referência e tente novamente."
              : "Você pode estar offline. Tente novamente em instantes."}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 mt-6 bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            data-testid="bible-chapter-retry"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {status === "ready" && chapter && (
        <article
          className="bg-white border border-border rounded-2xl p-6 sm:p-8 reading-max"
          data-testid="bible-chapter-content"
        >
          {chapter.source && chapter.source !== "api" && (
            <p className="text-[10px] uppercase tracking-[0.3em] text-navy/45 mb-4">
              {chapter.source === "cache" ? "Em cache" : "Modo offline"}
            </p>
          )}
          <div className={`space-y-4 text-navy/90 ${fontCls}`}>
            {chapter.verses.map((v) => {
              const isSelected = selectedVerse === v.verse;
              const isReading = ttsCurrentVerse === v.verse;
              const isFav = isFavoritePassage(verseRef(v.verse));
              return (
                <div
                  key={v.verse}
                  ref={(el) => { versesRef.current[v.verse] = el; }}
                  data-testid={`bible-verse-${v.verse}`}
                  data-selected={isSelected ? "true" : "false"}
                  data-reading={isReading ? "true" : "false"}
                  onClick={() =>
                    setSelectedVerse((cur) => (cur === v.verse ? null : v.verse))
                  }
                  className={`cursor-pointer rounded-xl px-3 -mx-3 py-2 transition-colors ${
                    isReading
                      ? "bg-gold/20 ring-1 ring-gold/40"
                      : isSelected
                        ? "bg-gold/10"
                        : "hover:bg-[hsl(var(--parchment))]"
                  }`}
                >
                  <p>
                    <sup
                      className="text-gold font-semibold mr-1.5 not-italic align-baseline"
                      style={{ fontSize: "0.72em" }}
                    >
                      {v.verse}
                    </sup>
                    <span className="font-serif">{v.text}</span>
                  </p>
                  {isSelected && (
                    <div
                      className="mt-3 pt-3 border-t border-border flex items-center gap-2"
                      data-testid={`bible-verse-actions-${v.verse}`}
                    >
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleShareVerse(v); }}
                        data-testid={`bible-verse-share-${v.verse}`}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-navy/70 hover:text-gold transition-colors"
                      >
                        <Share2 className="w-3.5 h-3.5" /> Compartilhar
                      </button>
                      <span className="text-navy/20">·</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleToggleFavorite(v); }}
                        data-testid={`bible-verse-favorite-${v.verse}`}
                        className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest transition-colors ${
                          isFav ? "text-gold" : "text-navy/70 hover:text-gold"
                        }`}
                      >
                        <Bookmark className="w-3.5 h-3.5" fill={isFav ? "currentColor" : "none"} />
                        {isFav ? "Salvo" : "Salvar"}
                      </button>
                      <span className="text-navy/20">·</span>
                      <div data-testid={`bible-verse-esperanca-${v.verse}`} onClick={(e) => e.stopPropagation()}>
                        <ShareButton
                          contentType="verse"
                          contentId={`${book.slug}-${requested}-${v.verse}`}
                          title={`${book.name} ${requested}.${v.verse}`}
                          mainText={v.text}
                          verse={v.text}
                          verseRef={`${book.name} ${requested}.${v.verse} (ARC)`}
                          theme={book.name}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </article>
      )}

      {/* Journal prompt, appears below the chapter content */}
      {status === "ready" && chapter && (
        <JournalPrompt
          refKey={`${book.slug}-${requested}`}
          refLabel={`${book.name} ${requested}, ${new Date().toLocaleDateString("pt-BR")}`}
          type="bible"
          bookSlug={book.slug}
          chapter={requested}
        />
      )}

      {/* Prev / Next chapter */}
      <div className="mt-8 flex items-center justify-between gap-3" data-testid="bible-chapter-nav">
        <Link
          to={prev ? `/biblia/${book.slug}/${prev}` : "#"}
          aria-disabled={!prev}
          data-testid="bible-chapter-prev"
          onClick={(e) => { if (!prev) e.preventDefault(); }}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors ${
            prev
              ? "bg-white border border-border text-navy hover:bg-navy hover:text-white"
              : "bg-transparent text-navy/30 cursor-not-allowed"
          }`}
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Capítulo anterior
        </Link>
        <span className="text-[10px] uppercase tracking-[0.3em] text-navy/40 hidden sm:inline">
          {requested} de {book.total_chapters}
        </span>
        <Link
          to={next ? `/biblia/${book.slug}/${next}` : "#"}
          aria-disabled={!next}
          data-testid="bible-chapter-next"
          onClick={(e) => { if (!next) e.preventDefault(); }}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors ${
            next
              ? "bg-navy text-white hover:bg-gold hover:text-navy"
              : "bg-transparent text-navy/30 cursor-not-allowed"
          }`}
        >
          Próximo capítulo <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div
          className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="bible-settings-modal"
          onClick={() => setShowSettings(false)}
        >
          <div
            className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55">Configurações</p>
                <h3 className="font-display text-2xl text-navy mt-1">Leitura</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                aria-label="Fechar"
                data-testid="bible-settings-close"
                className="w-8 h-8 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center text-navy/60 hover:text-gold"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
              Tamanho da fonte
            </p>
            <div className="grid grid-cols-3 gap-2" data-testid="bible-settings-font">
              {Object.entries(FONT_SIZES).map(([key, cfg]) => {
                const active = settings.fontSize === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => applyFontSize(key)}
                    data-testid={`bible-settings-font-${key}`}
                    data-active={active ? "true" : "false"}
                    className={`py-3 rounded-xl border text-sm font-display transition-colors ${
                      active
                        ? "bg-navy text-white border-navy"
                        : "bg-white border-border text-navy/70 hover:border-gold/50"
                    }`}
                  >
                    {cfg.label}
                  </button>
                );
              })}
            </div>

            <p className="text-[11px] text-navy/55 mt-5">
              Sua preferência é salva neste dispositivo.
            </p>
          </div>
        </div>
      )}

      {/* TTS floating control bar, appears while playing/paused */}
      {tts.supported && tts.playing && (
        <div
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 bg-navy text-white rounded-full shadow-2xl border border-gold/30 px-4 py-2.5 flex items-center gap-2"
          data-testid="bible-tts-bar"
          role="region"
          aria-label="Controles de leitura em voz alta"
        >
          <button
            type="button"
            onClick={tts.restart}
            aria-label="Reiniciar leitura"
            data-testid="bible-tts-restart"
            className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={tts.paused ? tts.resume : tts.pause}
            aria-label={tts.paused ? "Continuar" : "Pausar"}
            data-testid="bible-tts-toggle"
            className="w-9 h-9 rounded-full bg-gold text-navy hover:bg-white flex items-center justify-center transition-colors"
          >
            {tts.paused ? <Volume2 className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={tts.stop}
            aria-label="Parar"
            data-testid="bible-tts-stop"
            className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="border-l border-white/15 ml-1 pl-2 flex items-center gap-1">
            <Gauge className="w-3.5 h-3.5 text-gold" />
            <select
              value={tts.rate}
              onChange={(e) => tts.setRate(Number(e.target.value))}
              data-testid="bible-tts-rate"
              className="bg-transparent text-white text-xs uppercase tracking-widest focus:outline-none cursor-pointer"
            >
              {tts.rates.map((r) => (
                <option key={r} value={r} className="bg-navy text-white">
                  {r}x
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <ShareVerseModal
        open={!!shareVerse}
        onClose={() => setShareVerse(null)}
        verse={shareVerse}
      />
    </div>
  );
}
